import React, { useState } from "react";
import { adminAxios, errText } from "../AuthContext";
import { Plus, Upload, X, Loader2, File as FileIcon, Trash2 } from "lucide-react";

// Reusable helpers shared across the 12 tabs
export const Input = ({ label, value, onChange, type = "text", testid, ...rest }) => (
    <div>
        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">{label}</label>
        <input
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
            className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm bg-white"
            data-testid={testid}
            {...rest}
        />
    </div>
);

export const Textarea = ({ label, value, onChange, rows = 3, testid, ...rest }) => (
    <div>
        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">{label}</label>
        <textarea
            rows={rows}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm bg-white resize-none"
            data-testid={testid}
            {...rest}
        />
    </div>
);

export const Select = ({ label, value, onChange, options, testid, ...rest }) => (
    <div>
        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">{label}</label>
        <select
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm bg-white"
            data-testid={testid}
            {...rest}
        >
            {options.map((o) => (
                <option key={o} value={o}>{o}</option>
            ))}
        </select>
    </div>
);

export const Toggle = ({ label, value, onChange, testid }) => (
    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={testid}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-emerald-600" />
        <span className="text-sm text-stone-700">{label}</span>
    </label>
);

// File uploader component with multi-file drop
export const FileUploader = ({ onUploaded, multiple = false, accept = "image/*,application/pdf", testid }) => {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const handleFiles = async (files) => {
        setErr("");
        setBusy(true);
        const uploaded = [];
        try {
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await adminAxios().post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push({ url: data.url, name: data.name, type: data.type });
            }
            if (multiple) onUploaded(uploaded);
            else if (uploaded.length > 0) onUploaded(uploaded[0]);
        } catch (e) {
            setErr(errText(e, "Upload fallito"));
        } finally { setBusy(false); }
    };

    return (
        <div>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-emerald-300 bg-emerald-50/60 text-emerald-800 text-sm font-semibold cursor-pointer hover:bg-emerald-50 transition-colors" data-testid={testid}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {busy ? "Caricamento..." : "+ Allega file"}
                <input
                    type="file"
                    className="hidden"
                    accept={accept}
                    multiple={multiple}
                    onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                    disabled={busy}
                />
            </label>
            {err && <div className="text-red-600 text-xs mt-1">{err}</div>}
        </div>
    );
};

export const AttachmentsList = ({ items, onRemove, onUpdate, testid }) => (
    <div className="space-y-1.5 mt-2" data-testid={testid}>
        {items.length === 0 && <div className="text-xs text-stone-400">Nessun allegato</div>}
        {items.map((att, i) => (
            <div key={att.id || att.url || i} className="flex items-center gap-2 px-3 py-1.5 bg-stone-50 rounded-lg border border-stone-200 text-sm">
                <FileIcon size={14} className="text-emerald-600" />
                <a href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} target="_blank" rel="noreferrer" className="truncate text-stone-800 hover:text-emerald-700 shrink-0 max-w-[40%]">
                    {att.nome || att.name || att.url}
                </a>
                {onUpdate ? (
                    <input
                        type="text"
                        value={att.descrizione || ""}
                        onChange={(e) => onUpdate(i, { ...att, descrizione: e.target.value })}
                        placeholder="Descrizione (es. Visura catastale 2024)"
                        className="flex-1 px-2 py-1 border border-stone-200 rounded text-xs bg-white outline-none focus:border-emerald-500"
                        data-testid={`${testid}-desc-${i}`}
                    />
                ) : (
                    att.descrizione && <span className="flex-1 text-xs text-stone-600 italic">— {att.descrizione}</span>
                )}
                {onRemove && (
                    <button onClick={() => onRemove(i)} className="text-red-500 hover:text-red-700 shrink-0" data-testid={`${testid}-remove-${i}`}>
                        <Trash2 size={14} />
                    </button>
                )}
            </div>
        ))}
    </div>
);


// Full attachments section with descrizione input, file uploader and editable list
export const AttachmentsSection = ({ label, items, onAdd, onRemove, onUpdate, testid, accept }) => {
    const [pendingDesc, setPendingDesc] = React.useState("");
    return (
        <div className="pt-4 border-t border-stone-100" data-testid={testid}>
            <div className="text-sm font-semibold text-stone-700 mb-2">{label} ({items.length})</div>
            <div className="flex flex-col md:flex-row gap-2 items-stretch">
                <input
                    type="text"
                    value={pendingDesc}
                    onChange={(e) => setPendingDesc(e.target.value)}
                    placeholder="Descrizione allegato (es. Visura catastale del 2024)"
                    className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white outline-none focus:border-emerald-500"
                    data-testid={`${testid}-desc-input`}
                />
                <FileUploader
                    accept={accept || "image/*,application/pdf"}
                    onUploaded={(u) => {
                        onAdd({ ...u, descrizione: pendingDesc });
                        setPendingDesc("");
                    }}
                    testid={`${testid}-upload`}
                />
            </div>
            <AttachmentsList items={items} onRemove={onRemove} onUpdate={onUpdate} testid={`${testid}-list`} />
        </div>
    );
};
